import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from 'src/environments/environment';
import { PaginatedResult } from '../model/paginated-result';
import { ownErrorToastOptions } from '../interceptors/http-error.interceptor';
import { withConfirmPartial } from 'src/app/models/BroadcastFunds';

export interface BroadcastCallReport {
  total_sent: number;
  total_refused: number;
  total_answered_hangup: number;
  total_answered_dtmf: number;
  total_answered_listened: number;
  avg_duration_seconds: number;
  avg_listened_percentage: number;
  total_credits_used: number;
}

export interface BroadcastCallMetrics {
  total_calls: number;
  total_refused: number;
  total_answered_hangup: number;
  total_answered_dtmf: number;
  total_answered_listened: number;
  total_broadcasts: number;
}

export interface BroadcastCallStatus {
  id: string;
  status: string;
}

export interface BroadcastCallLeadList {
  id: string;
  name: string;
}

export interface BroadcastCallAudio {
  id: string;
  name: string;
  url?: string;
}

export interface BroadcastCall {
  id: string;
  user_id: string;
  name: string;
  status: BroadcastCallStatus;
  lead_list_id: string;
  call4u_audio_id: string;
  max_attempts: number;
  retry_interval_minutes: number;
  scheduled_date: string | null;
  scheduled_end_date: string | null;
  postback_url: string | null;
  webhook_hash: string | null;
  total_sent: number;
  total_answered: number;
  /**
   * Ligações que já saíram e ainda esperam o retorno do PABX (07/10/2026). Com o
   * envio "Concluído" e isto > 0, a tela mostra "Aguardando retorno": o desfecho —
   * e a cobrança ou devolução da reserva — ainda não veio.
   */
  awaiting_callbacks?: number;
  created_at: string;
  updated_at: string;
  leadList?: BroadcastCallLeadList;
  call4uAudio?: BroadcastCallAudio;
}

/** Call4uLead::STATUS_PROCESS_* do backend (coluna `status` do lead). */
export enum BroadcastCallContactStatus {
  Waiting = 0,
  Success = 1,
  Processing = 2,
  Error = 3,
}

export interface BroadcastCallContact {
  id: string;
  phone: string;
  /**
   * Rótulo já resolvido pelo backend (BroadcastCallService::statusLabel) — mesma fonte
   * do CSV. Opcional porque o front pode estar à frente do backend em deploy escalonado;
   * nesse caso o componente cai no cálculo local.
   */
  status_label?: string;
  /** Status interno do lead; distingue "Aguardando" de erro anterior ao PABX. */
  status?: BroadcastCallContactStatus;
  pabx_status: string | null;
  call_duration_seconds: number | null;
  listened_percentage: number | null;
  dtmf_response: string | null;
  sent_at: string | null;
  callback_received_at: string | null;
  tries: number;
  max_attempts: number;
  created_at: string;
}

export interface BroadcastCallWithReport {
  broadcast: BroadcastCall;
  report: BroadcastCallReport;
}

@Injectable({ providedIn: 'root' })
export class BroadcastCallService {

  private baseUrl = `${environment.apiUrl}/broadcast-calls`;

  constructor(private http: HttpClient) { }

  /**
   * `search` (LIKE pelo nome) e `status` (grupos `active`/`pending`/`completed`/`cancelled` —
   * ver `BroadcastCallController::STATUS_GROUPS` no backend) são opcionais; omitidos, o
   * comportamento é o mesmo de antes (lista completa do usuário, sem filtro).
   */
  getAll(page: number = 1, perPage: number = 15, search?: string, status?: string): Observable<PaginatedResult<BroadcastCall[]>> {
    let url = `${this.baseUrl}?page=${page}&per_page=${perPage}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }
    return this.http.get<PaginatedResult<BroadcastCall[]>>(url);
  }

  create(data: {
    name: string;
    lead_list_id: string;
    call4u_audio_id: string;
    max_attempts: number;
    retry_interval_minutes: number;
    scheduled_date?: string | null;
    scheduled_end_date?: string | null;
    postback_url?: string | null;
  }, confirmPartial = false): Observable<BroadcastCall> {
    // T.10: `confirmPartial` e `OWN_ERROR_TOAST` como no `BroadcastsService.send`.
    return this.http.post<BroadcastCall>(this.baseUrl, withConfirmPartial(data, confirmPartial), ownErrorToastOptions());
  }

  getById(id: string): Observable<BroadcastCallWithReport> {
    return this.http.get<BroadcastCallWithReport>(`${this.baseUrl}/${id}`);
  }

  getContacts(id: string, page: number = 1, perPage: number = 15, phone?: string): Observable<PaginatedResult<BroadcastCallContact[]>> {
    let url = `${this.baseUrl}/${id}/contacts?page=${page}&per_page=${perPage}`;
    if (phone) {
      url += `&phone=${encodeURIComponent(phone)}`;
    }
    return this.http.get<PaginatedResult<BroadcastCallContact[]>>(url);
  }

  update(id: string, data: Partial<{
    name: string;
    call4u_audio_id: string;
    scheduled_date: string | null;
    scheduled_end_date: string | null;
    max_attempts: number;
    retry_interval_minutes: number;
    postback_url: string | null;
  }>, confirmPartial = false): Observable<BroadcastCall> {
    // T.10: `confirmPartial` e `OWN_ERROR_TOAST` como no `BroadcastsService.update`.
    return this.http.put<BroadcastCall>(`${this.baseUrl}/${id}`, withConfirmPartial(data, confirmPartial), ownErrorToastOptions());
  }

  cancel(id: string): Observable<any> {
    return this.http.put(`${this.baseUrl}/${id}/cancel`, {});
  }

  pause(id: string): Observable<any> {
    return this.http.put(`${this.baseUrl}/${id}/pause`, {});
  }

  resume(id: string): Observable<any> {
    return this.http.put(`${this.baseUrl}/${id}/resume`, {});
  }

  exportCsv(id: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${id}/export-csv`, { responseType: 'blob' });
  }

  getMetrics(startDate: string, endDate: string, broadcastCallId?: string): Observable<BroadcastCallMetrics> {
    let url = `${this.baseUrl}/metrics?start_date=${startDate}&end_date=${endDate}`;
    if (broadcastCallId) {
      url += `&broadcast_call_id=${broadcastCallId}`;
    }
    return this.http.get<BroadcastCallMetrics>(url);
  }
}

/** Rótulo e cor do status do envio de ligação, com o "Aguardando retorno" (07/10/2026). */
export function broadcastCallStatusView(broadcast: Pick<BroadcastCall, 'status' | 'awaiting_callbacks'> | null): { label: string; cls: string } {
  const status = broadcast?.status?.status || '';
  const awaiting = broadcast?.awaiting_callbacks || 0;

  if ((status === 'Concluído' || status === 'Concluido') && awaiting > 0) {
    return { label: awaiting === 1 ? 'Aguardando retorno (1 ligação)' : `Aguardando retorno (${awaiting} ligações)`, cls: 'info' };
  }

  switch (status) {
    case 'Pendente': return { label: status, cls: 'warning' };
    case 'Processando':
    case 'Ligando': return { label: status, cls: 'info' };
    case 'Concluído':
    case 'Concluido': return { label: status, cls: 'success' };
    case 'Cancelado': return { label: status, cls: 'danger' };
    case 'Pausado': return { label: status, cls: 'secondary' };
    default: return { label: status || 'Desconhecido', cls: 'secondary' };
  }
}
